import { BigNumber } from 'bignumber.js';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import RentContract from '~/stores/rent-event-store/rent-contract';
import type {
  IHouseFixResource,
  IMarketHouseInterface,
  IUserHouseInterface,
  RentEventContractInterface
} from '~/stores/rent-event-store/rent-contract/rent-contract.interface';
import { FilterCondition, FilterType, type IFilterState, RentTab } from '~/stores/rent-event-store/types';
import { addLog } from '~/utils/prepare-win-logger';

export const useRentEventStore = defineStore('rent-event-store', () => {
  const { address } = useWeb3ModalAccount();
  const { getSigner, getProvider } = useProvider();
  const { blockchain } = useEnvs();
  const { sendContractMethod } = useSendContractMethod();
  const { t } = useI18n();
  const store = useMainStore();
  const updateInterval = ref<any>(null);

  const cache = new RuntimeCache();

  const contract: RentEventContractInterface = new RentContract({
    provider: getProvider(),
    signer: getSigner(),
    blockchain,
    sendContractMethod,
    t,
    updateStoreVersion: () => store.updateVersion()
  });

  const userHouses = ref<IUserHouseInterface[]>([]);
  const marketHouses = ref<IMarketHouseInterface[]>([]);
  const totalEarnedValue = ref<string>('0');
  const userClaimableEarnings = ref<string>('0');
  const announceModalOpen = ref(false);

  const isManorModalOpen = ref<boolean>(false);

  const selectedTab = ref<RentTab>(RentTab.AvailableHouses);

  const housesListFilters = ref<IFilterState>({
    condition: Object.values(FilterCondition),
    type: Object.values(FilterType)
  });

  const displayedHouses = ref<IUserHouseInterface[]>([]);
  const selectedHouses = ref<IUserHouseInterface[]>([]);

  watch(
    () => [selectedTab.value, housesListFilters.value, userHouses.value],
    (newVal, oldVal) => {
      // Only clear selection when tab or filters change, not on userHouses update
      const tabChanged = newVal[0] !== oldVal?.[0];
      const filtersChanged = newVal[1] !== oldVal?.[1];

      if (tabChanged || filtersChanged) {
        selectedHouses.value = [];
      } else {
        // Preserve selection by matching houses with existing selection using house keys
        const selectedKeys = selectedHouses.value.map(getHouseKey);
        selectedHouses.value = userHouses.value.filter((house) => selectedKeys.includes(getHouseKey(house)));
      }

      let filteredHouses = [...userHouses.value];

      if (selectedTab.value === RentTab.AvailableHouses) {
        filteredHouses = userHouses.value.filter((house) => !house.rentedOut);
      } else if (selectedTab.value === RentTab.StakedHouses) {
        filteredHouses = userHouses.value.filter((house) => house.rentedOut);
      }

      if (
        housesListFilters.value.type.length !== Object.keys(FilterType).length &&
        housesListFilters.value.type.length
      ) {
        filteredHouses = filteredHouses.filter((house) =>
          housesListFilters.value.type.includes(house.type as FilterType)
        );
      }

      if (
        housesListFilters.value.condition.length !== Object.keys(FilterCondition).length &&
        housesListFilters.value.condition.length
      ) {
        if (housesListFilters.value.condition.includes(FilterCondition.Good)) {
          filteredHouses = filteredHouses.filter((house) => house.durability === 100);
        } else if (housesListFilters.value.condition.includes(FilterCondition.NeedsRepair)) {
          filteredHouses = filteredHouses.filter((house) => house.durability < 100);
        }
      }
      displayedHouses.value = filteredHouses;
    },
    { immediate: true, deep: true }
  );

  const __updateUserData = async (force = false) => {
    addLog('updateUserData', address.value);
    if (!address.value) return;

    if (force) cache.invalidate(`userData-${address.value}`);
    const contractData = await cache.run(
      `userData-${address.value}`,
      async () => Promise.all([contract.getUserHouses(address.value!), contract.getUserEarnings(address.value!)]),
      10_000
    );

    addLog('contractData', contractData);

    userHouses.value = contractData[0];
    userClaimableEarnings.value = contractData[1].toString();
  };

  const __updateGlobalData = async (force = false) => {
    if (force) cache.invalidate(`updateGlobalData`);

    addLog('updateGlobalData', address.value);
    const contractData = await cache.run(
      'rentGlobalData',
      async () => Promise.all([contract.getMarketHouses(address.value)]),
      60_000 * 5
    );

    //addLog('contractData', contractData[0].toFixed(), contractData[1]);
    //totalEarnedValue.value = contractData[0].toString();
    marketHouses.value = contractData[0];
  };

  const getHouseFixResources = async (houses: IUserHouseInterface[]) => {
    const key = houses.map(getHouseKey).join('') + '_rent';
    const val = await cache.run(key, async () => contract.getHouseFixResources(houses), 3_000);
    addLog('getFixResources', houses);
    addLog('fixResources', val);
    return val;
  };

  const unstakeHouses = async (houses: IUserHouseInterface[]) => {
    if (houses.length === 0) return;
    await contract.unstakeHouses(houses);

    __updateUserData(true);
    __updateGlobalData(true);
  };

  const stakeHouses = async (houses: IUserHouseInterface[]) => {
    if (houses.length === 0) return;
    await contract.stakeHouses(houses);

    __updateUserData(true);
    __updateGlobalData(true);
  };

  const fixHouses = async (houses: IUserHouseInterface[]) => {
    if (houses.length === 0) return;
    await contract.fixHouses(houses);
    __updateUserData(true);
  };

  const getHouseKey = (house: IUserHouseInterface) => `${house.type}-${house.tokenId}-${house.address}`;

  const switchHouseSelection = (house: IUserHouseInterface) => {
    if (selectedHouses.value.some((h) => getHouseKey(h) === getHouseKey(house))) {
      selectedHouses.value = selectedHouses.value.filter((h) => h !== house);
    } else {
      selectedHouses.value.push(house);
    }
  };

  const switchTypeFilter = (type: FilterType) => {
    if (housesListFilters.value.type.includes(type)) {
      housesListFilters.value.type = housesListFilters.value.type.filter((t) => t !== type);
    } else {
      housesListFilters.value.type.push(type);
    }
  };

  const switchConditionFilter = (condition: FilterCondition) => {
    if (housesListFilters.value.condition.includes(condition)) {
      housesListFilters.value.condition = housesListFilters.value.condition.filter((t) => t !== condition);
    } else {
      housesListFilters.value.condition.push(condition);
    }
  };

  const getHouseFixSeasonPoints = (resources: IHouseFixResource[]) => {
    return cache.run(
      `fix-season-points-${JSON.stringify(resources)}`,
      async () => {
        return contract.getHouseFixSeasonPoints(resources);
      },
      CacheTime.Minute * 30
    );
  };

  // для condition сделать по типу

  const claimUserRewards = async () => {
    if (userClaimableEarnings.value === '0') return;
    await contract.claimUserEarnings();
    __updateUserData(true);
  };

  const getTotalEarnPerYear = (house: IUserHouseInterface) => {
    return (
      marketHouses.value.find(
        (h) => h.nftAddress.toLowerCase() === house.address.toLowerCase() && h.buildingType === house.buildingType
      )?.finalProfitPerYear || '0'
    );
  };

  const estimatedRewardPerYear = computed(() => {
    const rentedHouses = userHouses.value.filter((h) => h.rentedOut);
    return rentedHouses
      .reduce((sum, house) => sum.plus(getTotalEarnPerYear(house)), new BigNumber(0))
      .dp(2)
      .toFixed();
  });

  const hasBuildingsType = (house: IMarketHouseInterface) => {
    return userHouses.value.some(
      (h) =>
        h.address.toLowerCase() === house.nftAddress.toLowerCase() &&
        !h.rentedOut &&
        h.durability >= 100 &&
        h.buildingType === house.buildingType
    );
  };

  onMounted(async () => {
    await __updateGlobalData();

    updateInterval.value = setInterval(async () => {
      await __updateGlobalData();
      await __updateUserData();
    }, 30_000);
  });

  watch(
    address,
    async (newVal) => {
      if (newVal) contract.setupSigner(getSigner());
      await __updateUserData(true);
    },
    { immediate: true }
  );

  return {
    userHouses,
    totalEarnedValue,
    userClaimableEarnings,
    selectedTab,
    selectedHouses,
    isManorModalOpen,
    marketHouses,
    displayedHouses,
    housesListFilters,
    announceModalOpen,
    estimatedRewardPerYear,
    hasBuildingsType,

    getHouseFixResources,
    getHouseKey,
    unstakeHouses,
    stakeHouses,
    fixHouses,
    switchHouseSelection,
    switchTypeFilter,
    switchConditionFilter,
    claimUserRewards,
    getTotalEarnPerYear,
    getHouseFixSeasonPoints,

    __updateUserData,
    __updateGlobalData
  };
});
